import { useTranslation } from 'react-i18next'

import {
  createInvoice,
  duplicateDocument,
  getInvoiceFields,
  performDocumentsMassAction,
  sendVulcavoAttachmentFile,
  updateDirectory,
  updateDocument,
} from '../../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { SERVER_NON_FIELD_ERROR_KEY } from '../../../../constants/validation'
import {
  useMassActions,
  useNotification,
  usePerformMassAction,
} from '../../../../hooks'
import { isFolderFile } from '../../../../utils/file'
import { parseServerValidation } from '../../../../utils/validation'
import { ConfirmationModal } from '../../../Confirmation'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  useSendEmail,
} from '../../../Email'
import { getEntityActionTypeSelectOptionsEditOnly } from '../../../Entity'
import { useFileDownload, useFilesDownloadAll } from '../../../File'
import { InvoiceModal } from '../../../InvoiceModal'
import { useDocumentDuplicateToRelated } from '../../../Request'
import {
  DOCUMENTS_ACTION_DOWNLOAD,
  DOCUMENTS_ACTION_DOWNLOAD_MASS,
  DOCUMENTS_ACTION_DUPLICATE,
  DOCUMENTS_ACTION_DUPLICATE_TO_RELATED,
  DOCUMENTS_ACTION_EDIT,
  DOCUMENTS_ACTION_EDIT_ACCESS,
  DOCUMENTS_ACTION_EDIT_ACCESS_MASS,
  DOCUMENTS_ACTION_MARK_AS_INVOICE,
  DOCUMENTS_ACTION_MARK_AS_VULCAVO,
  DOCUMENTS_ACTION_MOVE_TO,
  DOCUMENTS_ACTION_MOVE_TO_MASS,
  DOCUMENTS_ACTION_REMOVE,
  DOCUMENTS_ACTION_REMOVE_MASS,
  DOCUMENTS_ACTION_SEND_VIA_EMAIL,
  DOCUMENTS_ACTION_SEND_VIA_EMAIL_MASS,
  DOCUMENTS_ACTION_VIEW_REPORT,
  DOCUMENTS_MASS_ACTION_NAMES,
} from '../../Document.constants'
import {
  getAsyncNotificationText,
  getNotificationTitle,
  isDraggingSelectedItem,
  isVulcavoFileTooLargeError,
} from '../../Document.utils'
import { DocumentEditAccessActionModal } from '../../DocumentEditAccessActionModal'
import { DocumentEditActionModal } from '../../DocumentEditActionModal'
import { DocumentMarkAsInvoiceConfirmModal } from '../../DocumentMarkAsInvoiceConfirmModal'
import { DocumentMoveToActionModal } from '../../DocumentMoveToActionModal'
import { DocumentViewReportModal } from '../../DocumentViewReportModal'

import {
  getDuplicateModalInitialNotifications,
  getEditInitialValues,
  getParent,
} from './useDocumentsTableActions.utils'

export function useDocumentsTableActions({
  downloadApiParams,
  excludeIds,
  filterValues,
  items,
  outboundUuid,
  pagination,
  parent,
  performMassActionApi,
  requestId,
  selectAllPages,
  selected,
  selectedIds,
  sendViaEmailProps = EMPTY_OBJECT,
  sub,
  unselectAllPages,
  onCleanSelection,
  onCloseEmailFormModal,
  onCloseModal,
  onReload,
  onSendViaEmail,
  onSetEmailFormModal,
  onSetModal,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const duplicateToRelated = useDocumentDuplicateToRelated({
    requestId,
    onReload,
  })

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText,
    getNotificationTitle,
  })

  function handleErrorAction(error) {
    onCloseModal()

    const serverError = parseServerValidation(error?.response?.data)

    handleAddErrorNotification({
      text: t(
        serverError?.[SERVER_NON_FIELD_ERROR_KEY] ||
          'Notification.DocumentsMassActionErrorKey'
      ),
      title: t('Notification.DocumentsMassActionServerErrorTitleKey'),
    })
  }

  async function performDefaultMassAction(data) {
    return performDocumentsMassAction({ data, outboundUuid })
  }

  const { isPerformingMassAction, massAction, setPerformingMassAction } =
    usePerformMassAction({
      excludeIds,
      filterValues,
      getAsyncTaskId,
      massActionNames: DOCUMENTS_MASS_ACTION_NAMES,
      pagination,
      performApi: performMassActionApi ?? performDefaultMassAction,
      selectAllPages,
      selectedIds,
      sub,
      onAddAsyncMassActionNotification: handleAddAsyncMassActionNotification,
      onAddFinishMassActionNotification: handleAddFinishMassActionNotification,
      onCleanSelection,
      onCloseModal,
      onErrorAction: handleErrorAction,
      onReload,
    })

  const {
    getRecipients,
    recipients = EMPTY_LIST,
    recipientsFilters,
  } = sendViaEmailProps

  // Scope recipients = select-all over the scope filters, loaded on send action only.
  const { handleOpenCreationEmail } = useSendEmail({
    excludeIds: EMPTY_LIST,
    filterValues: recipientsFilters,
    getRecipients,
    massMailing: false,
    selectAllPages: Boolean(recipientsFilters),
    onCloseEmailFormModal,
    onCloseModal,
    onSetEmailFormModal,
    onSetModal,
    onSuccessSending: unselectAllPages,
  })

  const { downloadFile } = useFileDownload({ apiParams: downloadApiParams })

  const { handleStartCheckStatus, isOverLimitFiles } = useFilesDownloadAll({
    onCloseModal,
    onSetModal,
  })

  function getHandleSubmitMassAction({ action, asyncTaskHandler, id }) {
    return ({ values }) => {
      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        asyncTaskHandler,
        value: values,
      })
    }
  }

  function handleDragDocumentEnd(event) {
    const activeId = event.active?.id
    const overId = event.over?.id

    if (!activeId || !overId || String(activeId) === String(overId)) {
      return
    }

    const parentDestination = getParent(overId, items)

    if (
      isFolderFile(parentDestination) &&
      parentDestination.permissions.can_write
    ) {
      const isDraggingSelection = isDraggingSelectedItem({
        activeId,
        excludeIds,
        selectAllPages,
        selectedIds,
      })

      massAction({
        ...(isDraggingSelection ? null : { ids: [activeId] }),
        action: DOCUMENTS_ACTION_MOVE_TO,
        value: { parent: parentDestination.id },
      })
    }
  }

  function handleSubmitEditAccess(values) {
    massAction({
      action: DOCUMENTS_ACTION_EDIT_ACCESS_MASS,
      value: values,
    })
  }

  const selectedFiles = selected.filter(item => !isFolderFile(item))

  const sendViaEmailFlowArgs = {
    closeEmailFormModal: onCloseEmailFormModal,
    closeModal: onCloseModal,
    setEmailFormModal: onSetEmailFormModal,
    setModal: onSetModal,
    unselectAllPages,
    onReload,
  }

  function showDeleteConfirmModal({ action, id }) {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t('Modal.DeleteDocumentBtnTextKey'),
        }}
        description={t('Modal.DeleteDocumentConfirmationTextKey')}
        onCancel={onCloseModal}
        onSubmit={getHandleSubmitMassAction({ action, id })}
      />
    )
  }

  function startDownloadAsyncTask({ status, task }) {
    handleStartCheckStatus(task, status)
  }

  function showDownloadConfirmModal({ action, item }) {
    const isOverLimit = isOverLimitFiles(item ? [item] : selectedFiles)

    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          ...(isOverLimit ? { Cancel: null } : null),
          submitText: t(
            isOverLimit
              ? 'Modal.CloseBtnKey'
              : 'Modal.DownloadDocumentBtnTextKey'
          ),
        }}
        description={t(
          isOverLimit
            ? 'Modal.AttentionFilesOutOfLimitMessageKey'
            : 'Modal.DownloadDocumentConfirmationTextKey'
        )}
        onCancel={onCloseModal}
        onSubmit={
          isOverLimit
            ? onCloseModal
            : getHandleSubmitMassAction({
                action,
                asyncTaskHandler: startDownloadAsyncTask,
                id: item?.id,
              })
        }
      />
    )
  }

  function handleSuccessAction(action, notifications) {
    onCloseModal()

    onReload().finally(() => {
      setPerformingMassAction(false)
    })

    if (notifications?.length > 0) {
      notifications.map(handleAddSuccessNotification)
    } else {
      handleAddSuccessNotification({
        title: getNotificationTitle({ action, t }),
      })
    }
  }

  function handleEditDocument(values, id) {
    setPerformingMassAction(true)

    updateDocument({ data: values, id, outboundUuid })
      .then(() => {
        handleSuccessAction(DOCUMENTS_ACTION_EDIT)
      })
      .catch(() => {
        handleErrorAction()
      })
  }

  function handleEditDirectory(values, id) {
    setPerformingMassAction(true)

    updateDirectory({ data: values, id, outboundUuid })
      .then(() => {
        handleSuccessAction(DOCUMENTS_ACTION_EDIT)
      })
      .catch(() => {
        handleErrorAction()
      })
  }

  function handleSubmitDuplicate(id, values) {
    setPerformingMassAction(true)

    return duplicateDocument({
      data: { directory: values.parent },
      id,
      outboundUuid,
    })
      .then(() => {
        handleSuccessAction(DOCUMENTS_ACTION_DUPLICATE)
      })
      .catch(() => {
        handleErrorAction()
      })
  }

  function handleDuplicateToRelated(item) {
    onCloseModal()
    duplicateToRelated([item.id])
  }

  async function handleSubmitMarkAsInvoice({ fields, invoiceFileId }) {
    return createInvoice({
      ...(invoiceFileId ? { invoice_file: invoiceFileId } : null),
      fields,
      request: requestId,
    })
  }

  async function handleOpenInvoiceModal(item) {
    if (!requestId) {
      handleErrorAction()

      return
    }

    try {
      const { data } = await getInvoiceFields(requestId)

      onCloseModal()
      onSetModal(
        <InvoiceModal
          withBudgetPlanNavigation
          documentId={item.id}
          fields={data?.invoice_fields ?? EMPTY_LIST}
          requestId={requestId}
          successTitleKey='DocumentMarkAsInvoice.MarkedAsInvoiceKey'
          onClose={onCloseModal}
          onSubmitRequest={handleSubmitMarkAsInvoice}
          onSuccess={onReload}
        />
      )
    } catch {
      handleErrorAction()
    }
  }

  function handleVulcavoErrorAction(error) {
    handleAddErrorNotification({
      text: t(
        isVulcavoFileTooLargeError(error)
          ? 'DocumentMarkAsVulcavo.FileTooLargeErrorKey'
          : 'DocumentMarkAsVulcavo.BuildingNotConfiguredErrorKey'
      ),
      title: t('DocumentMarkAsVulcavo.ErrorTitleKey'),
    })
  }

  function handleMarkAsVulcavo(item) {
    if (!requestId) {
      handleErrorAction()

      return
    }

    sendVulcavoAttachmentFile({ files: [item.id], request: requestId })
      .then(() => {
        onReload()
        handleAddSuccessNotification({
          title: t('DocumentMarkAsVulcavo.MarkedAsVulcavoKey'),
        })
      })
      .catch(handleVulcavoErrorAction)
  }

  function setItemAction(action, item) {
    switch (action) {
      case DOCUMENTS_ACTION_REMOVE:
        showDeleteConfirmModal({
          action: DOCUMENTS_ACTION_REMOVE,
          id: item.id,
        })
        break
      case DOCUMENTS_ACTION_DOWNLOAD:
        if (isFolderFile(item)) {
          showDownloadConfirmModal({
            action: DOCUMENTS_ACTION_DOWNLOAD,
            item,
          })
        } else {
          downloadFile(item)
        }

        break
      case DOCUMENTS_ACTION_SEND_VIA_EMAIL:
        if (onSendViaEmail) {
          onSendViaEmail({ ...sendViaEmailFlowArgs, files: [item] })
        } else {
          handleOpenCreationEmail({
            initialValues: {
              ...(recipients.length > 0
                ? {
                    [EMAIL_CREATION_FORM_FIELD_NAME_TO]: recipients,
                  }
                : null),
              [EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE]: [item],
            },
          })
        }

        break
      case DOCUMENTS_ACTION_EDIT:
        onSetModal(
          <DocumentEditActionModal
            itemAction
            formFieldsProps={{
              typeSelectProps: {
                disabled: true,
                options: getEntityActionTypeSelectOptionsEditOnly(),
              },
            }}
            id={item.id}
            initialValues={getEditInitialValues(item)}
            isFolder={isFolderFile(item)}
            onClose={onCloseModal}
            onSubmit={
              isFolderFile(item) ? handleEditDirectory : handleEditDocument
            }
          />
        )
        break
      case DOCUMENTS_ACTION_MOVE_TO:
        onSetModal(
          <DocumentMoveToActionModal
            parent={parent}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitMassAction({
              action: DOCUMENTS_ACTION_MOVE_TO,
              id: item.id,
            })}
          />
        )
        break
      case DOCUMENTS_ACTION_DUPLICATE:
        onSetModal(
          <DocumentMoveToActionModal
            initialNotifications={getDuplicateModalInitialNotifications(t)}
            parent={parent}
            submitProps={{
              title: 'Documents.DuplicateToFolderBtnKey',
            }}
            onClose={onCloseModal}
            onSubmit={({ values }) => handleSubmitDuplicate(item.id, values)}
          />
        )
        break
      case DOCUMENTS_ACTION_EDIT_ACCESS:
        onSetModal(
          <DocumentEditAccessActionModal
            id={item.id}
            initialValues={
              item.permission_groups_detail ?? item.permission_groups
            }
            isFolder={isFolderFile(item)}
            onClose={onCloseModal}
            onSubmit={
              isFolderFile(item) ? handleEditDirectory : handleEditDocument
            }
          />
        )
        break
      case DOCUMENTS_ACTION_VIEW_REPORT:
        onSetModal(
          <DocumentViewReportModal
            fileId={item.id}
            parentId={parent}
            onClose={onCloseModal}
          />
        )
        break

      case DOCUMENTS_ACTION_DUPLICATE_TO_RELATED:
        onSetModal(
          <ConfirmationModal
            actionButtonsProps={{
              submitText: t(
                'Modal.DuplicateDocumentToRelatedRequestsBtnTextKey'
              ),
            }}
            description={t(
              'Modal.DuplicateDocumentToRelatedRequestsConfirmationTextKey'
            )}
            onCancel={onCloseModal}
            onSubmit={() => handleDuplicateToRelated(item)}
          />
        )
        break

      case DOCUMENTS_ACTION_MARK_AS_INVOICE:
        onSetModal(
          <DocumentMarkAsInvoiceConfirmModal
            onClose={onCloseModal}
            onSubmit={() => handleOpenInvoiceModal(item)}
          />
        )
        break

      case DOCUMENTS_ACTION_MARK_AS_VULCAVO:
        handleMarkAsVulcavo(item)
        break
      default:
        return null
    }
  }

  function setMassAction(action) {
    switch (action) {
      case DOCUMENTS_ACTION_REMOVE_MASS:
        showDeleteConfirmModal({
          action: DOCUMENTS_ACTION_REMOVE_MASS,
        })
        break
      case DOCUMENTS_ACTION_DOWNLOAD_MASS:
        showDownloadConfirmModal({
          action: DOCUMENTS_ACTION_DOWNLOAD_MASS,
        })
        break
      case DOCUMENTS_ACTION_SEND_VIA_EMAIL_MASS:
        if (onSendViaEmail) {
          onSendViaEmail({ ...sendViaEmailFlowArgs, files: selectedFiles })
        } else {
          handleOpenCreationEmail({
            initialValues: {
              ...(recipients.length > 0
                ? {
                    [EMAIL_CREATION_FORM_FIELD_NAME_TO]: recipients,
                  }
                : null),
              [EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE]:
                selectedFiles,
            },
          })
        }

        break
      case DOCUMENTS_ACTION_MOVE_TO_MASS:
        onSetModal(
          <DocumentMoveToActionModal
            parent={parent}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitMassAction({
              action: DOCUMENTS_ACTION_MOVE_TO_MASS,
            })}
          />
        )
        break
      case DOCUMENTS_ACTION_EDIT_ACCESS_MASS:
        onSetModal(
          <DocumentEditAccessActionModal
            inheritField
            checkNoChanges={false}
            onClose={onCloseModal}
            onSubmit={handleSubmitEditAccess}
          />
        )
        break
      default:
        return null
    }
  }

  return {
    handleDragDocumentEnd,
    isPerformingMassAction,
    selectedFiles,
    setItemAction,
    setMassAction,
  }
}
